import { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import CommandAnalyticsStrip from '../components/analytics/CommandAnalyticsStrip';
import api from '../api';
import {
  formatDateTime,
  formatNumber,
} from '../utils/formatters';

const REVIEW_ACTIONS = [
  ['under_manual_review', 'Review'],
  ['needs_retry', 'Retry'],
  ['approved', 'Approve'],
  ['clear', 'Clear'],
];

function buildTrendBuckets(users = [], bucketCount = 8) {
  const now = new Date();
  const buckets = Array.from({ length: bucketCount }, (_, index) => {
    const start = new Date(now);
    start.setDate(now.getDate() - (bucketCount - index - 1) * 4);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 4);
    return {
      start,
      end,
      value: 0,
      label: start.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }).toUpperCase(),
    };
  });

  for (const user of users) {
    const source = user.lastAppealAt || user.updatedAt || user.createdAt;
    const date = source ? new Date(source) : null;
    if (!date || Number.isNaN(date.getTime())) continue;
    const match = buckets.find((bucket) => date >= bucket.start && date < bucket.end);
    if (match) match.value += 1;
  }

  const max = Math.max(...buckets.map((bucket) => bucket.value), 1);
  return buckets.map((bucket, index) => ({
    ...bucket,
    height: `${20 + (bucket.value / max) * 65}%`,
    highlight: index >= bucketCount - 2,
  }));
}

function averageReviewMinutes(users = []) {
  const values = users
    .map((user) => {
      const source = user.lastAppealAt || user.updatedAt || user.createdAt;
      const date = source ? new Date(source) : null;
      if (!date || Number.isNaN(date.getTime())) return null;
      return Math.max(1, Math.round((Date.now() - date.getTime()) / 60000));
    })
    .filter((value) => value != null);

  if (!values.length) return 0;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

function formatMinutesAsClock(minutes) {
  const total = Number(minutes || 0);
  const hours = String(Math.floor(total / 60)).padStart(2, '0');
  const mins = String(total % 60).padStart(2, '0');
  return `${hours}:${mins}`;
}

function actionLabelForUser(user) {
  if (user.reviewStatus === 'needs_retry') return 'Retry Guidance';
  if (user.reviewStatus === 'under_manual_review') return 'Biometric Re-check';
  if (user.reviewStatus === 'appeal_received') return 'Appeal Intake';
  if (user.docVerified && user.faceVerified) return 'Pass Through';
  return 'Document Review';
}

function resultTone(status) {
  if (['approved', 'clear'].includes(status)) {
    return 'border-[#ffcc00] text-[#ffedc3] bg-[#ffcc00]/10';
  }
  if (status === 'needs_retry') {
    return 'border-[#fd6c00] text-[#fd6c00] bg-[#fd6c00]/10';
  }
  return 'border-[#93000a] text-[#ffb4ab] bg-[#93000a]/10';
}

export default function VerificationDesk() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workingId, setWorkingId] = useState('');
  const [summary, setSummary] = useState({});
  const [users, setUsers] = useState([]);
  const [commandSummary, setCommandSummary] = useState(null);
  const [refreshingCommand, setRefreshingCommand] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [response, commandResponse] = await Promise.all([
        api.get('/admin/platform/verification-queue'),
        api.get('/admin/platform/command-analytics'),
      ]);
      setSummary(response.data.summary || {});
      setUsers(response.data.users || []);
      setCommandSummary(commandResponse.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load the verification desk');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function refreshCommandAnalytics() {
    setRefreshingCommand(true);
    try {
      const { data } = await api.post('/admin/platform/command-analytics/refresh', {
        range: '24h',
      });
      setCommandSummary(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not refresh command analytics');
    } finally {
      setRefreshingCommand(false);
    }
  }

  async function updateReview(userId, status) {
    const note = window.prompt('Optional review note for this user:', '') ?? '';
    setWorkingId(`${userId}:${status}`);
    try {
      await api.post(`/admin/platform/verification-queue/${userId}/review`, { status, note });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not update verification review');
    } finally {
      setWorkingId('');
    }
  }

  const model = useMemo(() => {
    const pendingUsers = users.filter((user) => !user.isVerified);
    const urgentUsers = users.filter((user) =>
      ['appeal_received', 'under_manual_review', 'needs_retry'].includes(user.reviewStatus)
    );
    const verifiedUsers = users.filter((user) => user.docVerified && user.faceVerified);
    const flaggedUsers = users.filter((user) => user.reviewStatus === 'needs_retry');
    const trend = buildTrendBuckets(users);
    const averageMinutes = averageReviewMinutes(urgentUsers);
    const verifiedPercent = users.length ? Math.round((verifiedUsers.length / users.length) * 100) : 0;
    const pendingPercent = users.length ? Math.round((pendingUsers.length / users.length) * 100) : 0;
    const flaggedPercent = users.length ? Math.round((flaggedUsers.length / users.length) * 100) : 0;

    return {
      pendingUsers,
      urgentUsers,
      verifiedUsers,
      flaggedUsers,
      trend,
      averageMinutes,
      verifiedPercent,
      pendingPercent,
      flaggedPercent,
      actionsRows: users.slice(0, 4),
      appealRows: urgentUsers.length ? urgentUsers : users.slice(0, 5),
    };
  }, [users]);

  return (
    <Layout searchPlaceholder="Search pipeline ID...">
      <div className="space-y-8">
        {error ? (
          <div className="rounded-lg border border-[#93000a] bg-[#2a1112] px-4 py-3 text-sm text-[#ffdad6]">
            {error}
          </div>
        ) : null}

        {commandSummary ? (
          <CommandAnalyticsStrip
            summary={commandSummary}
            title="Verification context freshness"
            description="Identity review stays Shimbirr-local, but the wider business and system context below depends on the shared command telemetry staying current."
            onRefresh={refreshCommandAnalytics}
            refreshing={refreshingCommand}
            compact
          />
        ) : null}

        <section className="space-y-8">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <MetricCard
              title="ACTIVE PIPELINE QUEUE"
              value={formatNumber(summary.total || 0)}
              meta={`+${formatNumber(summary.manualReview || 0)} since 0800h`}
              accent="primary"
              barWidth="72%"
            />
            <MetricCard
              title="AVG REVIEW TIME"
              value={formatMinutesAsClock(model.averageMinutes)}
              meta="STATUS: UNDER STRAIN"
              subValue="-12%"
            />
            <MetricCard
              title="CRITICAL FLAGS"
              value={formatNumber(model.urgentUsers.length)}
              meta="MANUAL AUDIT REQUIRED"
              accent="secondary"
              icon="priority_high"
            />
            <MetricCard
              title="SYSTEM THROUGHPUT"
              value={`${model.verifiedPercent}%`}
              meta="NODE STABILITY: HIGH"
            />
          </div>

          {loading ? (
            <div className="rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6 text-sm text-[#d2c5ab]">
              Loading verification queue...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6 lg:col-span-8">
                  <div className="mb-6 flex items-center justify-between">
                    <h2 className="flex items-center gap-2 font-['Geist'] text-2xl font-bold text-white">
                      <MaterialIcon name="analytics" className="text-[#ffcc00]" />
                      Appeal Success Rates (30D)
                    </h2>
                    <div className="rounded border border-[#4e4632] bg-[#353534] px-3 py-1 text-sm text-[#e5e2e1]">
                      EXPORT DATA
                    </div>
                  </div>

                  <div className="flex h-64 items-end gap-1 border-b border-l border-[#4e4632] px-4">
                    <div className="relative flex h-full w-full items-end justify-between">
                      <div className="pointer-events-none absolute inset-0 flex justify-between opacity-10">
                        {Array.from({ length: 5 }).map((_, index) => (
                          <div key={index} className="h-full w-px bg-white" />
                        ))}
                      </div>
                      {model.trend.map((point) => (
                        <div
                          key={point.label}
                          className={`glow-yellow h-3 w-3 rounded-full ${point.highlight ? 'bg-[#ffedc3]' : 'bg-[#ffcc00]'}`}
                          style={{ marginBottom: point.height }}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 flex justify-between px-2 text-xs text-[#d2c5ab]">
                    {model.trend.slice(0, 5).map((point) => (
                      <span key={point.label}>{point.label}</span>
                    ))}
                  </div>
                </div>

                <div className="col-span-12 rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6 lg:col-span-4">
                  <h2 className="mb-6 font-['Geist'] text-2xl font-bold text-white">Identity Verification</h2>
                  <ProgressRow label="Pending Review" value={formatNumber(summary.pendingIdentity || 0)} width={`${model.pendingPercent}%`} tone="bg-[#f1c100]" />
                  <ProgressRow label="Verified" value={formatNumber(model.verifiedUsers.length)} width={`${model.verifiedPercent}%`} tone="bg-[#ffedc3]" />
                  <ProgressRow label="Flagged/Risk" value={formatNumber(model.flaggedUsers.length)} width={`${model.flaggedPercent}%`} tone="bg-[#fd6c00]" />

                  <div className="mt-8 border-t border-[#4e4632] pt-6">
                    <div className="flex items-center gap-2 rounded border border-[#4e4632] bg-[#353534] p-3">
                      <MaterialIcon name="security_update_warning" className="text-[#fd6c00]" />
                      <div>
                        <p className="text-sm text-white">Automated rejection rate up 4%</p>
                        <p className="text-xs text-[#d2c5ab]">Rule: Region-Mismatch-Alpha</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-3 rounded-xl border-l-4 border-[#fd6c00] bg-[#2a2a2a] p-6 lg:col-span-2">
                  <div className="flex items-start gap-4">
                    <div className="rounded-lg bg-[#fd6c00] p-3 text-[#341100]">
                      <MaterialIcon name="priority_high" className="text-2xl" />
                    </div>
                    <div>
                      <h3 className="font-['Geist'] text-2xl font-bold text-white">
                        Critical Policy Alert: Region CN-22
                      </h3>
                      <p className="mt-1 text-[#d2c5ab]">
                        Manual overrides for high-risk appeals are currently constrained. All pending appeals must be flagged for secondary compliance audit before manual status change.
                      </p>
                      <div className="mt-4 flex gap-4">
                        <button className="text-sm font-bold uppercase text-[#fd6c00] hover:underline">
                          View Protocol
                        </button>
                        <button className="text-sm font-bold uppercase text-[#d2c5ab] hover:underline">
                          Acknowledge
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-span-3 flex flex-col justify-between rounded-xl border border-[#4e4632] bg-[#201f1f] p-6 lg:col-span-1">
                  <div>
                    <div className="flex items-center gap-2 text-[#ffedc3]">
                      <MaterialIcon name="lightbulb" className="text-xl" />
                      <span className="text-sm font-bold uppercase tracking-[0.16em]">Retry Guidance</span>
                    </div>
                    <p className="mt-3 text-white">
                      Passport glares detected in 18% of recent manual batches. Instruct users to disable flash.
                    </p>
                  </div>
                  <button className="mt-4 rounded-lg border border-[#4e4632] py-2 text-sm text-[#d2c5ab] transition hover:text-white">
                    Update Auto-Response
                  </button>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-[#4e4632] bg-[#1c1b1b]">
                <div className="flex flex-col gap-4 border-b border-[#4e4632] bg-[#201f1f] px-6 py-4 lg:flex-row lg:items-center lg:justify-between">
                  <h3 className="font-['Geist'] text-2xl font-bold text-white">Active Identity Appeals</h3>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-[#d2c5ab]">Filter by:</span>
                    <div className="flex gap-2">
                      <span className="rounded-full border border-[#4e4632] bg-[#353534] px-3 py-1 text-sm text-white">
                        All ({formatNumber(model.appealRows.length)})
                      </span>
                      <span className="rounded-full border border-[#4e4632] px-3 py-1 text-sm text-[#d2c5ab]">
                        Critical ({formatNumber(model.flaggedUsers.length)})
                      </span>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-[#201f1f] text-[#d2c5ab]">
                      <tr>
                        <th className="px-6 py-3 text-xs font-bold uppercase">Identity ID</th>
                        <th className="px-6 py-3 text-xs font-bold uppercase">Submission Date</th>
                        <th className="px-6 py-3 text-xs font-bold uppercase">Type</th>
                        <th className="px-6 py-3 text-xs font-bold uppercase">Complexity</th>
                        <th className="px-6 py-3 text-xs font-bold uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-bold uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#4e4632]">
                      {model.appealRows.map((user) => (
                        <tr key={user.id} className="group transition-colors hover:bg-[#201f1f]">
                          <td className="px-6 py-4">
                            <div className={`flex flex-col ${user.reviewStatus === 'needs_retry' ? 'border-l-2 border-[#fd6c00] pl-4' : ''}`}>
                              <span className="font-bold text-white">#{user.shimbirrId || user.id.slice(-8).toUpperCase()}</span>
                              <span className="text-sm text-[#d2c5ab]">User: {user.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">{formatDateTime(user.lastAppealAt || user.updatedAt || user.createdAt)}</td>
                          <td className="px-6 py-4">
                            <span className="rounded border border-[#4e4632] bg-[#353534] px-2 py-1 text-sm">
                              {user.hasDocument ? 'Passport / ID' : 'Profile Retry'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-1">
                              {Array.from({ length: 3 }).map((_, index) => (
                                <div
                                  key={index}
                                  className={`h-4 w-1.5 rounded-full ${
                                    index < (user.docVerified && user.faceVerified ? 3 : user.hasDocument && user.hasSelfie ? 2 : 1)
                                      ? 'bg-[#ffedc3]'
                                      : 'bg-[#353534]'
                                  }`}
                                />
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <StatusChip status={user.reviewStatus} />
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              {REVIEW_ACTIONS.map(([status, label]) => (
                                <button
                                  key={status}
                                  type="button"
                                  onClick={() => updateReview(user.id, status)}
                                  disabled={workingId === `${user.id}:${status}`}
                                  className={`rounded px-3 py-2 text-xs font-bold transition ${
                                    status === 'approved'
                                      ? 'bg-[#fd6c00] text-white'
                                      : 'border border-[#4e4632] text-[#ffedc3] hover:bg-[#2a2a2a]'
                                  }`}
                                >
                                  {workingId === `${user.id}:${status}` ? '...' : label}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-[#201f1f] p-4 text-center">
                  <button className="text-sm font-bold text-[#ffedc3] hover:underline">
                    Load More Appeal Requests
                  </button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-[#4e4632] bg-[#1c1b1b]">
                <div className="flex items-center justify-between border-b border-[#4e4632] bg-[#201f1f] p-6">
                  <h2 className="font-['Geist'] text-2xl font-bold text-white">Recent Pipeline Actions</h2>
                  <div className="flex gap-4">
                    <button className="flex items-center gap-1 text-sm text-[#d2c5ab] hover:text-[#ffcc00]">
                      <MaterialIcon name="filter_list" className="text-sm" /> FILTER
                    </button>
                    <button className="flex items-center gap-1 text-sm text-[#d2c5ab] hover:text-[#ffcc00]">
                      <MaterialIcon name="download" className="text-sm" /> CSV
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-[#4e4632] bg-[#0e0e0e] text-[#d2c5ab]">
                        <th className="p-4 text-xs font-bold uppercase">Pipeline ID</th>
                        <th className="p-4 text-xs font-bold uppercase">Action Type</th>
                        <th className="p-4 text-xs font-bold uppercase">Operator</th>
                        <th className="p-4 text-xs font-bold uppercase">Timestamp</th>
                        <th className="p-4 text-xs font-bold uppercase">Result</th>
                        <th className="p-4 text-xs font-bold uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#4e4632]">
                      {model.actionsRows.map((user) => (
                        <tr key={user.id} className="transition-colors hover:bg-[#2a2a2a]">
                          <td className="p-4 font-semibold text-[#ffedc3]">
                            #{user.shimbirrId || user.id.slice(-8).toUpperCase()}
                          </td>
                          <td className="p-4">{actionLabelForUser(user)}</td>
                          <td className="p-4 text-[#ffedc3]">
                            {user.reviewStatus === 'under_manual_review' ? 'ADMIN-OPS' : 'SYS-AUTO-01'}
                          </td>
                          <td className="p-4 text-[#d2c5ab]">{formatDateTime(user.updatedAt || user.createdAt)}</td>
                          <td className="p-4">
                            <span className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${resultTone(user.reviewStatus)}`}>
                              {['approved', 'clear'].includes(user.reviewStatus) ? 'PASS' : user.reviewStatus === 'needs_retry' ? 'FLAGGED' : 'REVIEW'}
                            </span>
                          </td>
                          <td className="p-4">
                            <button className="text-[#d2c5ab] transition hover:text-white">
                              <MaterialIcon name="open_in_new" className="text-base" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </section>

        {!loading && model.urgentUsers.length ? (
          <div className="fixed right-8 top-24 z-50 hidden max-w-sm flex-col gap-4 xl:flex">
            <div className="flex items-start gap-4 rounded-lg border border-[#fd6c00] bg-[#353534] p-4 shadow-2xl backdrop-blur-md">
              <div className="text-[#fd6c00]">
                <MaterialIcon name="warning" className="text-xl" />
              </div>
              <div>
                <p className="text-lg font-bold text-[#e5e2e1]">Queue Density Warning</p>
                <p className="mt-1 text-sm text-[#d2c5ab]">
                  Manual review queue has exceeded {formatNumber(model.urgentUsers.length)} items. Processing delays expected.
                </p>
              </div>
              <button className="text-[#d2c5ab] hover:text-white">
                <MaterialIcon name="close" className="text-sm" />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </Layout>
  );
}

function MetricCard({ title, value, meta, subValue, accent = 'neutral', barWidth = '0%', icon = 'fiber_manual_record' }) {
  return (
    <div className={`rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6 ${accent === 'primary' ? 'glow-yellow' : ''}`}>
      <div className="absolute" />
      <span className="mb-2 block text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">{title}</span>
      <div className="flex items-baseline gap-2">
        <span className={`font-['Geist'] text-5xl font-black ${accent === 'secondary' ? 'text-[#fd6c00]' : 'text-white'}`}>
          {value}
        </span>
        {subValue ? (
          <span className={`text-sm font-semibold ${accent === 'secondary' ? 'text-[#fd6c00]' : 'text-[#ffb4ab]'}`}>
            {subValue}
          </span>
        ) : null}
        {accent === 'secondary' ? <MaterialIcon name={icon} className="text-[#fd6c00]" /> : null}
      </div>
      <p className="mt-4 text-xs uppercase tracking-[0.18em] text-[#d2c5ab]">{meta}</p>
      {barWidth !== '0%' ? (
        <div className="mt-4 h-1 w-full rounded bg-[#353534]">
          <div className="h-full bg-[#f1c100]" style={{ width: barWidth }} />
        </div>
      ) : null}
    </div>
  );
}

function ProgressRow({ label, value, width, tone }) {
  return (
    <div className="mb-6">
      <div className="mb-2 flex justify-between">
        <span className="text-base text-[#d2c5ab]">{label}</span>
        <span className={`font-semibold ${label === 'Flagged/Risk' ? 'text-[#fd6c00]' : 'text-white'}`}>{value}</span>
      </div>
      <div className="h-4 overflow-hidden rounded-full bg-[#353534]">
        <div className={`h-full ${tone}`} style={{ width }} />
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  if (status === 'needs_retry') {
    return (
      <div className="flex items-center gap-2">
        <MaterialIcon name="error" className="text-sm text-[#fd6c00]" />
        <span className="text-sm font-bold uppercase text-[#fd6c00]">Fraud Flag</span>
      </div>
    );
  }

  if (status === 'under_manual_review') {
    return (
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="status-pulse absolute inline-flex h-full w-full rounded-full bg-[#fd6c00]" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-[#fd6c00]" />
        </span>
        <span className="text-sm font-bold uppercase text-[#fd6c00]">Urgent Review</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="h-2 w-2 rounded-full bg-[#9a9078]" />
      <span className="text-sm font-bold uppercase text-[#d2c5ab]">Pending Queue</span>
    </div>
  );
}
